const HOURS_PER_DAY = 8;

const parseXerDate = (value) => {
  if (!value || !value.trim()) return null;
  const normalized = value.trim().replace(" ", "T");
  const date = new Date(normalized);
  return Number.isNaN(date.getTime()) ? null : date;
};

const parseNumber = (value) => {
  const num = Number(value);
  return Number.isNaN(num) ? 0 : num;
};

const diffDays = (start, end) => {
  if (!start || !end) return 0;
  const ms = end.getTime() - start.getTime();
  return Math.max(0, Math.round(ms / (1000 * 60 * 60 * 24)));
};

const pickTaskDates = (task) => {
  const start =
    parseXerDate(task.act_start_date) ||
    parseXerDate(task.early_start_date) ||
    parseXerDate(task.target_start_date) ||
    parseXerDate(task.restart_date);

  const end =
    parseXerDate(task.act_end_date) ||
    parseXerDate(task.early_end_date) ||
    parseXerDate(task.target_end_date) ||
    parseXerDate(task.reend_date);

  return { start, end };
};

const buildTableMap = (content) => {
  const tables = {};
  const lines = content.split(/\r?\n/);
  let currentTable = null;
  let fields = [];

  lines.forEach((line) => {
    if (!line.trim()) return;
    const parts = line.split("\t");
    const marker = parts[0];

    if (marker === "%T") {
      currentTable = parts[1];
      tables[currentTable] = { fields: [], rows: [] };
      return;
    }

    if (!currentTable) return;

    if (marker === "%F") {
      fields = parts.slice(1);
      tables[currentTable].fields = fields;
      return;
    }

    if (marker === "%R") {
      const values = parts.slice(1);
      const row = {};
      fields.forEach((field, index) => {
        row[field] = values[index] ?? "";
      });
      tables[currentTable].rows.push(row);
    }
  });

  return tables;
};

const rollupDates = (node) => {
  let minStart = node.start;
  let maxEnd = node.end;

  node.children.forEach((child) => {
    const rolled = rollupDates(child);
    if (rolled.start && (!minStart || rolled.start < minStart)) {
      minStart = rolled.start;
    }
    if (rolled.end && (!maxEnd || rolled.end > maxEnd)) {
      maxEnd = rolled.end;
    }
  });

  if (node.type === "wbs") {
    node.start = minStart;
    node.end = maxEnd;
    node.durationDays =
      minStart && maxEnd ? diffDays(minStart, maxEnd) : node.durationDays;
  }

  return { start: minStart, end: maxEnd };
};

const buildWbsTree = (wbsRows, tasksByWbs) => {
  const nodesById = {};

  wbsRows.forEach((wbs) => {
    nodesById[wbs.wbs_id] = {
      key: `wbs-${wbs.wbs_id}`,
      id: wbs.wbs_short_name || wbs.wbs_id,
      name: wbs.wbs_name || wbs.wbs_short_name || "Untitled WBS",
      type: "wbs",
      wbsId: wbs.wbs_id,
      parentWbsId: wbs.parent_wbs_id || null,
      start: null,
      end: null,
      durationDays: 0,
      percentComplete: null,
      isSummary: true,
      isCritical: false,
      taskType: "summary",
      children: [],
    };
  });

  Object.values(nodesById).forEach((node) => {
    const taskRows = tasksByWbs[node.wbsId] || [];
    taskRows.forEach((task) => {
      const { start, end } = pickTaskDates(task);
      const durationHours = parseNumber(
        task.target_drtn_hr_cnt || task.remain_drtn_hr_cnt
      );
      const durationDays =
        start && end
          ? diffDays(start, end)
          : Math.round(durationHours / HOURS_PER_DAY);

      node.children.push({
        key: `task-${task.task_id}`,
        id: task.task_code || task.task_id,
        name: task.task_name || task.task_code || "Untitled Task",
        type: "task",
        taskId: task.task_id,
        wbsId: task.wbs_id,
        start,
        end,
        durationDays,
        percentComplete: parseNumber(task.phys_complete_pct),
        isSummary: false,
        isCritical:
          task.driving_path_flag === "Y" || parseNumber(task.total_float_hr_cnt) === 0,
        taskType: task.task_type || "TT_Task",
        statusCode: task.status_code,
        children: [],
      });
    });
  });

  const roots = [];
  Object.values(nodesById).forEach((node) => {
    if (node.parentWbsId && nodesById[node.parentWbsId]) {
      nodesById[node.parentWbsId].children.push(node);
    } else {
      roots.push(node);
    }
  });

  const sortNodes = (items) => {
    items.sort((a, b) => {
      if (a.type !== b.type) {
        return a.type === "wbs" ? -1 : 1;
      }
      return String(a.id).localeCompare(String(b.id), undefined, {
        numeric: true,
      });
    });
    items.forEach((item) => {
      if (item.children.length > 0) {
        sortNodes(item.children);
      }
    });
  };

  sortNodes(roots);
  roots.forEach(rollupDates);
  return roots;
};

const flattenVisibleRows = (nodes, expandedKeys, depth = 0, acc = []) => {
  nodes.forEach((node) => {
    acc.push({ ...node, depth });
    if (node.children.length > 0 && expandedKeys.includes(node.key)) {
      flattenVisibleRows(node.children, expandedKeys, depth + 1, acc);
    }
  });
  return acc;
};

const collectDefaultExpandedKeys = (nodes, depth = 0, acc = []) => {
  nodes.forEach((node) => {
    if (node.children.length > 0 && depth < 2) {
      acc.push(node.key);
      collectDefaultExpandedKeys(node.children, depth + 1, acc);
    }
  });
  return acc;
};

export const parseXerFile = (content) => {
  const tables = buildTableMap(content);
  const projectRow = tables.PROJECT?.rows?.[0] || {};
  const wbsRows = tables.PROJWBS?.rows || [];
  const taskRows = tables.TASK?.rows || [];
  const dependencyRows = tables.TASKPRED?.rows || [];

  const tasksByWbs = taskRows.reduce((acc, task) => {
    const wbsId = task.wbs_id;
    if (!acc[wbsId]) acc[wbsId] = [];
    acc[wbsId].push(task);
    return acc;
  }, {});

  const tree = buildWbsTree(wbsRows, tasksByWbs);
  const expandedKeys = collectDefaultExpandedKeys(tree);

  const dependencies = dependencyRows
    .map((dep) => ({
      from: `task-${dep.pred_task_id}`,
      to: `task-${dep.task_id}`,
      type: dep.pred_type,
      lagHours: parseNumber(dep.lag_hr_cnt),
    }))
    .filter((dep) => dep.from && dep.to);

  return {
    projectName:
      projectRow.proj_short_name ||
      projectRow.add_by_name ||
      "Schedule",
    planStartDate: parseXerDate(projectRow.plan_start_date),
    planEndDate: parseXerDate(projectRow.plan_end_date),
    tree,
    expandedKeys,
    dependencies,
    stats: {
      wbsCount: wbsRows.length,
      taskCount: taskRows.length,
      dependencyCount: dependencyRows.length,
    },
  };
};

export const flattenScheduleTree = (tree, expandedKeys) =>
  flattenVisibleRows(tree, expandedKeys);

export const formatScheduleDate = (date) => {
  if (!date) return "—";
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

export const formatDuration = (days, taskType) => {
  if (taskType === "TT_Mile" || taskType === "TT_FinMile") {
    return "0 d";
  }
  if (!days && days !== 0) return "—";
  return `${days} d`;
};

export const SCHEDULE_ACCEPTED_EXTENSIONS = [
  ".mpp",
  ".mpx",
  ".xer",
  ".pp",
  ".xml",
  ".ppx",
];

export const isXerFile = (fileName) =>
  fileName.toLowerCase().endsWith(".xer");

export const isMppFile = (fileName) =>
  fileName.toLowerCase().endsWith(".mpp");

export const isPreviewableScheduleFile = (fileName) =>
  isXerFile(fileName) || isMppFile(fileName);

const deserializeDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const mapTreeDates = (nodes, mapper) =>
  nodes.map((node) => ({
    ...node,
    start: mapper(node.start),
    end: mapper(node.end),
    children: node.children?.length
      ? mapTreeDates(node.children, mapper)
      : [],
  }));

export const serializeScheduleForUpload = (schedule) => ({
  scheduleProjectName: schedule.projectName,
  stats: {
    wbs_count: schedule.stats.wbsCount ?? schedule.stats.wbs_count ?? 0,
    task_count: schedule.stats.taskCount ?? schedule.stats.task_count ?? 0,
    dependency_count:
      schedule.stats.dependencyCount ?? schedule.stats.dependency_count ?? 0,
  },
});

export const rehydrateSchedule = (schedule) => ({
  ...schedule,
  planStartDate: deserializeDate(schedule.planStartDate),
  planEndDate: deserializeDate(schedule.planEndDate),
  tree: mapTreeDates(schedule.tree || [], deserializeDate),
});
